import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import { Modal, Button } from "react-bootstrap";

import { getFavProductsv2, requestStock } from "../../actions/products";
import { getCart, addCart } from "../../actions/cart";
import Pagination from "./Pagination";

import { Productv2 } from "./productv2";

import NoImage from "../../assets/img/unavailable.svg";
import styled from "styled-components";
import fuzzysort from "fuzzysort";

const StyledPaginationProducts = styled.div`
  ${(props) =>
    props.brand &&
    `
    .pagination .active a {
      background: ${props.brand.colors.cartBackground} !important;

      &:hover {
        color: ${props.brand.colors.white} !important;
      }
    }
  `}
`;

const StyledSortBy = styled.div`
  @media (max-width: 1024px) {
    max-width: 230px;
  }
`;

const StyledSortButton = styled.button`
  width: 100% !important;
  min-width: 130px;
`;

export const Productsv2 = ({
  page,
  view,
  setView,
  name,
  shopFont,
  category,
  isLoading,
  setIsLoading,
  brand,
  currentPage,
  setCurrentPage,
}) => {
  const location = useLocation();
  const history = useHistory();
  const sortInitialState = name ? "Best Match" : "A-Z";
  const auth = JSON.parse(localStorage.getItem("profile"));
  const productsData = useSelector((state) => state.products);
  const cart = useSelector((state) => state.cart);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filter, setFilter] = useState("name");
  const [order, setOrder] = useState("ASC");
  const [sortBy, setSortBy] = useState(sortInitialState);

  // const [sorting, setSorting] = useState({filter: 'name', order: 'ASC', sortBy: 'A-Z'})
  const [stockSort, setStockSort] = useState(true);
  
  const productPerPage = 20;
  const numPages = Math.ceil(filteredProducts.length / productPerPage);
  let totalInPage = currentPage * productPerPage;
  let startCount = 1;
  if (currentPage > 1) startCount = (currentPage - 1) * productPerPage + 1;
  if (currentPage === numPages) totalInPage = filteredProducts.length;

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [search, setSearch] = useState("");
  const [showModal, setShow] = useState(false);
  const dispatch = useDispatch();

  const handleClose = () => setShow(false);

  // Optimized product filtering with improved performance and cleaner logic
  const filterProducts = (sortOptions) => {
    const { filter, order, bestMatch } = sortOptions;
    let filtered = [];

    // Step 1: Get base filtered products based on category
    if (page === "shop") {
      switch (category) {
        case "Favorites":
          filtered = getFavoriteProducts();
          break;
        case "Specials":
          filtered = getSpecialProducts();
          break;
        case "Premier's Choice":
        case "Year End Sale":
          filtered = getCategoryProducts(category);
          break;
        default:
          filtered = getRegularProducts(category);
      }
    } else {
      // Search page
      filtered = fuzzysort.go(name, productsData.productsv2, {
        keys: ["name", "ndc"],
      });
    }

    // Step 2: Apply preferred status
    filtered = applyPreferredStatus(filtered);

    // Step 3: Sort products
    const sorted = sortProducts(filtered, { filter, order, bestMatch });

    // Step 4: Apply stock sorting
    const stockSorted = applyStockSorting(sorted);

    // Step 5: Apply custom pricing
    const finalProducts = applyCustomPricing(stockSorted);

    setFilteredProducts(finalProducts);
    setIsLoading(false);
  };

  // Helper functions for cleaner code organization
  const getFavoriteProducts = () => {
    const preferredIds = new Set((productsData.prefproduct || []).map(p => p.productId));
    const filtered = [];

    // Add favorite products with custom pricing
    if (productsData.favproductv2) {
      productsData.favproductv2.forEach(favProd => {
        if (preferredIds.has(favProd.id)) {
          filtered.push(favProd);
        }
      });
    }

    // Add missing preferred products from main list
    productsData.prefproduct.forEach(prefProd => {
      const existsInFiltered = filtered.some(prod => prod.id === prefProd.productId);
      if (!existsInFiltered) {
        const product = productsData.productsv2.find(prod => prod.id === prefProd.productId);
        if (product) filtered.push(product);
      }
    });

    return fuzzysort.go(search, filtered, { keys: ["name", "ndc"], all: true });
  };

  const getSpecialProducts = () => {
    const shortDatedIds = new Set(productsData.shortDatedProducts?.map(p => p.id) || []);
    const shortDatedProducts = productsData.productsv2?.filter(product => 
      shortDatedIds.has(product.id)
    ) || [];
    
    return fuzzysort.go("", shortDatedProducts, { keys: ["name", "ndc"], all: true });
  };

  const getCategoryProducts = (categoryName) => {
    const categoryProducts = productsData.productsv2.filter(
      product => product?.category_2 === categoryName
    );
    
    return fuzzysort.go("", categoryProducts, { keys: ["name"], all: true });
  };

  const getRegularProducts = (categoryName) => {
    return fuzzysort.go(categoryName, productsData.productsv2, { keys: ["category"] });
  };

  const applyPreferredStatus = (filtered) => {
    const preferredIds = new Set((productsData.prefproduct || []).map(p => p.productId));
    
    return filtered.map(item => ({
      ...item,
      obj: {
        ...item.obj,
        preferred: preferredIds.has(item.obj?.id)
      }
    }));
  };

  const sortProducts = (filtered, { filter, order, bestMatch }) => {
    if (bestMatch) {
      return filtered.sort((a, b) => b.score - a.score);
    }

    const sortFn = order === "ASC" 
      ? (a, b) => a.obj[filter] > b.obj[filter] ? 1 : a.obj[filter] === b.obj[filter] ? 0 : -1
      : (a, b) => a.obj[filter] > b.obj[filter] ? -1 : a.obj[filter] === b.obj[filter] ? 0 : 1;

    return filtered.sort(sortFn);
  };

  const applyStockSorting = (sorted) => {
    if (stockSort) {
      const inStock = sorted.filter(product => product.obj.totalquantityonhand > 0);
      const outOfStock = sorted.filter(product => product.obj.totalquantityonhand === 0);
      return [...inStock, ...outOfStock];
    }
    return sorted.filter(product => product.obj.totalquantityonhand === 0);
  };

  const applyCustomPricing = (sorted) => {
    let result = [...sorted];

    // Apply favorite product pricing
    if (productsData.favproductv2) {
      const favPriceMap = new Map(
        productsData.favproductv2.map(favProd => [favProd.id, favProd.price])
      );

      result = result.map(item => {
        const favPrice = favPriceMap.get(item.obj.id);
        if (favPrice) {
          return {
            ...item,
            obj: {
              ...item.obj,
              favorite: true,
              price: parseFloat(favPrice) || parseFloat(item.obj.price)
            }
          };
        }
        return item;
      });
    }

    // Apply sales pricing
    if (productsData.salesPrice) {
      const salesPriceMap = new Map(
        productsData.salesPrice.map(saleProd => [Number(saleProd.id), saleProd.price])
      );

      result = result.map(item => {
        const salePrice = salesPriceMap.get(item.obj.id);
        if (salePrice) {
          return {
            ...item,
            obj: {
              ...item.obj,
              price: Math.min(parseFloat(salePrice), parseFloat(item.obj.price))
            }
          };
        }
        return item;
      });
    }

    return result;
  };

  const renderPage = () => {
    let rows = [];
    for (
      let i = (currentPage - 1) * productPerPage;
      i < currentPage * productPerPage && i < filteredProducts.length;
      i++
    ) {
      let product = filteredProducts[i];

      rows.push(
        <Productv2
          brand={brand}
          shopFont={shopFont}
          view={view}
          index={i}
          key={product.obj.id}
          product={product.obj}
          addCart={handleAddCart}
          requestStock={handleRequestStock}
          setSelectedProduct={setSelectedProduct}
          selectedProduct={selectedProduct}
          isLoading={isLoading}
          isCartLoading={isCartLoading}
          requestLoading={requestLoading}
          quantity={quantity}
          setQuantity={setQuantity}
          cart={cart}
          category={product.obj.category || ""}
          selectedCategory={category}
          sortBy={sortBy}
          stockSort={stockSort}
        />
      );
    }
    return rows;
  };

  const handleAddCart = (product) => {
    const user = JSON.parse(localStorage.getItem("profile"));

    const newProduct = {
      product: {
        productId: parseInt(product.id),
        productName: product.name,
        productNumber: product.productNumber,
        price: parseFloat(product.price),
        imageUrl: product.imageUrl,
        quantity,
        ndc: product.ndc,
        bottleSize: product.bottleSize,
        manufacturer: product.manufacturer,
        category: product.category,
        expirationDate: product.expirationDate,
        lotName: product.lotName,
      },
    };

    setIsCartLoading(true);
    setSelectedProduct(product);
    dispatch(addCart(user?.username, newProduct));
  };

  const handleSortCheck = (e) => {
    let value = e.target.checked;
    setStockSort(value);
  };


  const handleSearchFav = (e) => {
    const searchNDC = e.target.value.replaceAll("-", "");
    setIsLoading(false);
    setSearch(searchNDC);
  };

  const handleRequestStock = (product) => {
    const user = JSON.parse(localStorage.getItem("profile"));
    const formData = {
      ndc: product.ndc,
      productName: product.name,
    };
    setRequestLoading(true);
    setSelectedProduct(product);
    dispatch(requestStock(user?.username, formData));
  };

  // We still need to track favorite actions to prevent page resets
  const prevPrefProductLength = useRef(productsData.prefproduct?.length || 0);
  const prevFavProductsLength = useRef(productsData.favproductv2?.length || 0);
  
  useEffect(() => {
    const { productsv2, favproductv2, prefproduct, shortDatedProducts } =
      productsData;
    
    // Check if this update is due to a favorite action
    const currentPrefLength = prefproduct?.length || 0;
    const currentFavLength = favproductv2?.length || 0;
    
    // Detect changes in favorites
    const prefLengthChanged = currentPrefLength !== prevPrefProductLength.current;
    const favLengthChanged = currentFavLength !== prevFavProductsLength.current;
    
    // Update refs for next comparison
    prevPrefProductLength.current = currentPrefLength;
    prevFavProductsLength.current = currentFavLength;
    
    // Check for isFavoriteAction metadata in the last action
    const lastAction = typeof window !== 'undefined' && 
                      window.__REDUX_DEVTOOLS_EXTENSION__ && 
                      window.__REDUX_DEVTOOLS_EXTENSION__.getLastAction && 
                      window.__REDUX_DEVTOOLS_EXTENSION__.getLastAction();
    
    // Determine if this is a favorite action
    const isFavoriteAction = 
      (lastAction && lastAction.meta && lastAction.meta.isFavoriteAction) || 
      (lastAction && lastAction.type === 'UPDATE_PRODUCT_PREFERRED') ||
      prefLengthChanged || 
      favLengthChanged;
    
    // IMPORTANT: We completely skip URL manipulation for favorite actions
    // Only reset URL page parameter if it's a new search or category change
    // AND it's not a favorite action
    if (!requestLoading && !isFavoriteAction) {
      // For category or name changes, we might want to reset to page 1
      // But we'll let the ShopV2 component handle this now
    }
    
    requestLoading && setShow(true);
    setRequestLoading(false);

    if (
      (productsv2 &&
        favproductv2 &&
        prefproduct &&
        shortDatedProducts &&
        auth?.username) ||
      (productsv2 && shortDatedProducts && !auth?.username)
    ) {
      if (sortBy === "Best Match") {
        filterProducts({ filter, order, bestMatch: true });
      } else {
        filterProducts({ filter, order, bestMatch: false });
      }
    }
  }, [productsData, category, name, stockSort, search]);

  useEffect(() => {
    setQuantity(1);
    setSelectedProduct(null);
    setIsCartLoading(false);
    // setIsLoading(false);
  }, [cart]);

  useEffect(() => {
    // dispatch(getFavProductsv2(auth?.username))
    dispatch(getCart(auth?.username));
  }, []);

  return (
    <div className="products-container">
      <div
        className={
          page === "search"
            ? "d-flex justify-content-between top-details align-items-start search-wrapper"
            : "d-flex justify-content-between top-details"
        }
      >
        {page === "search" ? (
          <h3 className="search-for">
            Search Results for <q>{name}</q>
          </h3>
        ) : (
          //   <p className="total-products">
          //     {isLoading
          //       ? "Showing..."
          //       : filteredProducts.length === 0
          //       ? ""
          //       : `Showing ${startCount} - ${totalInPage} of ${filteredProducts.length} products`}
          //   </p>
          <h2>{category !== "" && category === "Specials" ? "Short Dated Specials" : category !== "" ? category : "All Products"}</h2>
        )}
        <div className="d-flex align-items-center filter-view-container">
          {page === "shop" && category === "Favorites" && (
            <div className="search-container">
              <input
                type="text"
                placeholder="Search Favorite Product..."
                onChange={handleSearchFav}
              />
            </div>
          )}
          <StyledSortBy className="d-flex align-items-center sort-by">
            <label className="sort-label d-flex">
              Sort by:
              <div className="dropdown">
                <StyledSortButton
                  className="dropdown-toggle"
                  type="button"
                  id="sortByDropdown"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  {sortBy}
                </StyledSortButton>
                <div className="dropdown-menu" aria-labelledby="sortByDropdown">
                  {page !== "shop" && (
                    <span
                      className="dropdown-item"
                      onClick={() => {
                        setFilter("name");
                        setOrder("ASC");
                        setSortBy("Best Match");
                        filterProducts({
                          filter: "name",
                          order: "ASC",
                          bestMatch: true,
                        });
                      }}
                    >
                      Best Match
                    </span>
                  )}

                  <span
                    className="dropdown-item"
                    onClick={() => {
                      setFilter("name");
                      setOrder("ASC");
                      setSortBy("A-Z");
                      filterProducts({
                        filter: "name",
                        order: "ASC",
                        bestMatch: false,
                      });
                    }}
                  >
                    A - Z
                  </span>
                  <span
                    className="dropdown-item"
                    onClick={() => {
                      setFilter("name");
                      setOrder("DESC");
                      setSortBy("Z-A");
                      filterProducts({
                        filter: "name",
                        order: "DESC",
                        bestMatch: false,
                      });
                    }}
                  >
                    Z - A
                  </span>

                  <span
                    className="dropdown-item"
                    onClick={() => {
                      setFilter("price");
                      setOrder("ASC");
                      setSortBy("$ Low - High");
                      filterProducts({
                        filter: "price",
                        order: "ASC",
                        bestMatch: false,
                      });
                    }}
                  >
                    $ Low - High
                  </span>
                  <span
                    className="dropdown-item"
                    onClick={() => {
                      setFilter("price");
                      setOrder("DESC");
                      setSortBy("$ High - Low");
                      filterProducts({
                        filter: "price",
                        order: "DESC",
                        bestMatch: false,
                      });
                    }}
                  >
                    $ High - Low
                  </span>
                  <hr />
                  <div className="flex align-items-center dropdown-item">
                    <input
                      className="mr-2"
                      type="checkbox"
                      id="sortStock"
                      name="sortStock"
                      defaultChecked={stockSort}
                      onChange={handleSortCheck}
                    />
                    <label
                      className="sort-stock-label mb-0"
                      htmlFor="sortStock"
                    >
                      {" "}
                      Stock
                    </label>
                  </div>
                </div>
              </div>
            </label>
          </StyledSortBy>
          <div className="d-flex align-items-center view">
            <button
              className={"grid-btn" + (view === "grid" ? " active" : "")}
              onClick={() => setView("grid")}
            ></button>
            <button
              className={"list-btn" + (view === "list" ? " active" : "")}
              onClick={() => setView("list")}
            ></button>
          </div>
        </div>
      </div>
      <div className={"products" + (view === "list" ? " list-view" : "")}>
        {/* <div className="list-header">
          <div className="no-container">
            <p>Item #</p>
            <p>NDC</p>
          </div>
          <div className="name-container">
            <p>Name</p>
          </div>
          <p className="company">Manufacturer</p>
          <p className="size">Size</p>
          <p className="strength">Strength</p>
          <div
            className="price-container"
            style={{ minWidth: category !== "Favorites" && "100px" }}
          >
            <p>Price</p>
          </div>
          <p className="size">PPU</p>
          <p
            className={"buy" + (!auth ? " buy-offline" : " buy-online")}
            style={!auth ? { minWidth: "90px" } : { minWidth: "145px" }}
          >
            Buy
          </p>
          <div className={"incart" + (!auth ? " d-none" : " d-block")}>
            <p>In</p>
            <p>Cart</p>
          </div>
        </div> */}
        <div className="row">
          {/* <div className="container-fluid">
            <div className="spinner-container d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                   <span className="sr-only">Loading...</span>
                 </div>
               </div>
             </div> */}
          {isLoading && search !== "" && (
            <div className="col-12 d-flex align-items-center justify-content-center text-center">
              Searching products...
            </div>
          )}
          {isLoading && search === "" && (
            <div className="col-12 d-flex align-items-center justify-content-center text-center">
              Loading products...
            </div>
          )}
          {!isLoading && filteredProducts.length > 0 && renderPage()}
          {!isLoading && filteredProducts.length === 0 && (
            <div className="col-12 d-flex align-items-center justify-content-center text-center">
              No Products
            </div>
          )}
        </div>
      </div>
      <StyledPaginationProducts brand={brand} className="pagination-products">
        <Pagination
          currentPage={currentPage}
          totalPages={numPages}
          onPageChange={setCurrentPage}
          category={category}
        />
      </StyledPaginationProducts>
      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {productsData.requestStockSuccess
              ? "Request Sent"
              : "Request Error"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {productsData.requestStockSuccess
            ? productsData.salesRep === undefined //If customer is not assigned with a sales rep, they will send an email to sales@premierpharma.com
              ? `Thanks! sales@premierpharma.com will be in touch with you shortly.`
              : `Sales Representative ${productsData.salesRep} will be in touch with you shortly.`
            : "Please try again"}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
